/**
 * 알림 메시지 아이템 컴포넌트
 */

'use client'

import { useRouter } from 'next/navigation'
import { CheckCircle, XCircle } from 'lucide-react'
import type { NotificationMessage, NotificationAction } from '@/types/notification'
import { getMessageIcon, getMessageBgColor, formatNotificationDate } from '@/utils/notification-helpers'

interface NotificationMessageItemProps {
  message: NotificationMessage
  processingMessageId: string | number | null
  onAction: (action: NotificationAction) => Promise<void>
  scope?: 'client' | 'freelancer' // scope에 따라 다른 경로로 라우팅
}

export const NotificationMessageItem = ({
  message,
  processingMessageId,
  onAction,
  scope = 'freelancer', // 기본값은 freelancer
}: NotificationMessageItemProps) => {
  const router = useRouter()
  const isProcessing = processingMessageId === message.id

  // 알림 클릭 시 이동할 경로 결정
  const getClickPath = () => {
    const payload = (message as any).payload || {}
    const inquiryId = payload?.inquiryId ? Number(payload.inquiryId) : null

    if (message.type === 'public_inquiry_created') {
      return inquiryId ? `/ops/inquiries/${inquiryId}` : '/ops/inquiries'
    }

    // Client scope 처리
    if (scope === 'client') {
      // 견적서 제출 알림은 프로젝트 상세 페이지로 이동
      if (message.counsel_id && (
        message.type === 'estimate_submitted' ||
        message.type === 'team_to_client_estimate_request'
      )) {
        return `/client/projects/${message.counsel_id}`
      }
      // 기본 인박스로 이동
      return '/client/inbox'
    }

    // Freelancer scope 처리
    if (message.type === 'team_invite' || message.type === 'team_proposal') {
      return '/my/freelancer/teams'
    }
    if (message.type === 'join_request') {
      return '/my/freelancer/teams?tab=settings'
    }
    // 프로젝트 매칭 알림은 프로젝트 상세 페이지로 이동
    if (message.type === 'project_matched' || message.type === 'counsel_matched_to_team') {
      if (message.counsel_id) {
        return `/freelancer/projects/${message.counsel_id}`
      }
      return '/freelancer/inbox'
    }
    // 견적 관련 알림도 프로젝트 상세 페이지로 이동
    if (message.counsel_id && (
      message.type === 'client_to_team_estimate_request' ||
      message.type === 'client_to_individual_estimate_request' ||
      message.type === 'estimate_submitted' ||
      message.type === 'estimate_accepted' ||
      message.type === 'estimate_rejected'
    )) {
      return `/freelancer/projects/${message.counsel_id}`
    }
    // 다른 알림 타입은 기본 인박스로 이동
    return null
  }

  const clickPath = getClickPath()

  return (
    <div
      className={`px-5 py-4 border-b border-gray-100 last:border-b-0 hover:bg-gradient-to-r hover:from-gray-50/50 hover:to-blue-50/30 transition-all duration-200 ${
        clickPath ? 'cursor-pointer' : ''
      }`}
      onClick={() => {
        if (clickPath && !isProcessing) {
          router.push(clickPath)
        }
      }}
    >
      <div className="flex items-start gap-3">
        {/* 아이콘 */}
        <div className={`w-11 h-11 ${getMessageBgColor(message.type)} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
          {getMessageIcon(message.type)}
        </div>

        {/* 내용 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <p className="text-sm font-bold text-gray-900">{message.title}</p>
            <span className="text-[10px] text-gray-400 whitespace-nowrap font-medium">
              {formatNotificationDate(message.created_at)}
            </span>
          </div>
          {message.projectTitle && (
            <p className="text-xs font-semibold text-gray-800 mb-1">
              {message.projectTitle}
            </p>
          )}
          <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-2">
            {message.content}
          </p>

          {/* 수락/거절 버튼 */}
          {(message.canAccept || message.canDecline) && (
            <div className="flex gap-2">
              {message.canAccept && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAction('accept')
                  }}
                  disabled={isProcessing}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-3.5 h-3.5" />
                      수락
                    </>
                  )}
                </button>
              )}
              {message.canDecline && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAction('decline')
                  }}
                  disabled={isProcessing}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-gray-700 border-t-transparent rounded-full animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-3.5 h-3.5" />
                      거절
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

